import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiMessageSquare, FiX, FiSend, FiLoader, FiCpu } = FiIcons;

function AIAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hello! I\'m your AI sales assistant. How can I help you today?' }
  ]);
  const [isTyping, setIsTyping] = useState(false);

  const handleSend = async () => {
    if (!input.trim()) return;
    
    // Add user message to chat
    const userMessage = { role: 'user', content: input };
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInput('');
    setIsTyping(true);
    
    try {
      // In a real implementation, this would call the OpenAI API
      // For now, we'll simulate a response after a delay
      setTimeout(() => {
        const assistantResponse = generateSimpleResponse(input);
        setMessages(prevMessages => [
          ...prevMessages,
          { role: 'assistant', content: assistantResponse }
        ]);
        setIsTyping(false);
      }, 1000);
    } catch (error) {
      console.error('Error getting AI response:', error);
      setMessages(prevMessages => [
        ...prevMessages,
        { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }
      ]);
      setIsTyping(false);
    }
  };

  // Simple response generator for demo purposes
  const generateSimpleResponse = (query) => {
    query = query.toLowerCase();
    
    if (query.includes('hello') || query.includes('hi')) {
      return 'Hello! How can I assist you with your sales today?';
    }
    else if (query.includes('vehicle') || query.includes('car') || query.includes('inventory')) {
      return 'I can help you find the perfect vehicle for your customer. Would you like me to suggest features to highlight based on customer preferences?';
    }
    else if (query.includes('lead') || query.includes('prospect')) {
      return 'For new leads, I recommend starting with open-ended questions about their lifestyle and vehicle needs. Would you like me to generate some qualification questions?';
    }
    else if (query.includes('email') || query.includes('follow')) {
      return 'I can help draft follow-up emails to customers. Just provide me with the customer name, vehicle of interest, and any previous interaction notes.';
    }
    else if (query.includes('price') || query.includes('negotiate') || query.includes('deal')) {
      return 'When negotiating, focus on the value proposition rather than just the price. Highlight unique features, warranty benefits, and after-sales service to justify the price point.';
    }
    else {
      return 'I\'m here to help with sales strategies, customer interactions, vehicle information, and more. Could you provide more details about what you need assistance with?';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        className="fixed bottom-6 right-6 p-4 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-colors z-50"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <SafeIcon icon={isOpen ? FiX : FiMessageSquare} className="w-6 h-6" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-20 right-6 w-80 sm:w-96 bg-white rounded-lg shadow-xl z-50 flex flex-col border border-gray-200"
            style={{ maxHeight: 'calc(100vh - 150px)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between bg-primary-600 text-white px-4 py-3 rounded-t-lg">
              <div className="flex items-center">
                <SafeIcon icon={FiCpu} className="w-5 h-5 mr-2" />
                <h3 className="font-medium">AI Sales Assistant</h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-primary-700 rounded transition-colors"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 p-4 overflow-y-auto flex flex-col gap-3" style={{ maxHeight: '350px' }}>
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-lg ${
                      message.role === 'user'
                        ? 'bg-primary-100 text-gray-800 rounded-br-none'
                        : 'bg-gray-100 text-gray-800 rounded-bl-none'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] p-3 rounded-lg bg-gray-100 text-gray-800 rounded-bl-none">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-gray-200">
              <div className="flex items-center bg-gray-100 rounded-lg px-3 py-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask me anything about sales..."
                  className="flex-1 bg-transparent border-none focus:ring-0 resize-none text-sm max-h-20 min-h-[40px]"
                  rows={1}
                  disabled={isTyping}
                />
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSend}
                  disabled={!input.trim() || isTyping}
                  className={`p-2 rounded-full ${
                    !input.trim() || isTyping ? 'text-gray-400' : 'text-primary-600 hover:bg-primary-50'
                  } transition-colors`}
                >
                  {isTyping ? (
                    <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
                  ) : (
                    <SafeIcon icon={FiSend} className="w-5 h-5" />
                  )}
                </motion.button>
              </div>
              <div className="text-xs text-gray-400 text-center mt-2">
                Powered by OpenAI
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default AIAssistant;